/**
 * PromotionConfirmModal — minors-exit promotion-cost confirmation.
 *
 * Layer 4 of the 4-layer promotion-modal invariant (Tony 2026-05-13):
 *   even if Layer 1 (rosterMoveGuards.isPriceLessPlayer) incorrectly
 *   returned true for a player with an existing contract, this modal
 *   MUST NOT render the promotion-charge UI. It re-checks the player's
 *   canonical contract_salary / contract_type on render. If the player
 *   is contract-bearing, the modal auto-fires onConfirm so the parent's
 *   move dispatches normally (backend Layer 2 + executor Layer 3 will
 *   then correctly route to the no-charge re-promotion path).
 *
 * Visual pattern mirrors the inline ConfirmModal in MinorLeagueDraft.js
 * for design consistency.
 */
import React, { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';


const PromotionConfirmModal = ({
  isOpen,
  player,           // Layer 4 defense-in-depth — required (Tony 2026-05-13)
  playerName,
  destination,
  cost,
  contractYears,
  onConfirm,
  onCancel,
}) => {
  // Defense-in-depth shape check. Mirrors rosterMoveGuards.isPriceLessPlayer
  // semantics: canonical contract_salary first, fall back to JSONB-write
  // shape salary; either signal of a real contract suppresses the charge UI.
  const financial =
    player?.league_context?.financial ??
    player?.roster_data?.financial ??
    {};
  const salaryRaw = financial.contract_salary ?? financial.salary;
  const contractType = financial.contract_type;
  const hasExistingContract =
    (salaryRaw != null && Number(salaryRaw) > 0) ||
    (contractType != null && contractType !== '0' && contractType !== '');

  useEffect(() => {
    if (isOpen && hasExistingContract) {
      onConfirm?.();
    }
    // Intentionally omit onConfirm — would re-fire if the parent re-creates
    // the handler reference. The modal unmounts after onConfirm anyway
    // (parent clears pendingPromotion), so the effect runs at most once
    // per mount with a contract-bearing player.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasExistingContract]);

  if (!isOpen) return null;
  // Layer 4 invariant: never render charge UI for a contract-bearing player.
  if (hasExistingContract) return null;

  const destinationLabel = destination === 'active' ? 'active roster' : 'bench';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Activate Player Confirmation"
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
    >
      <div
        className={`${dynastyTheme.components.card.base} border ${dynastyTheme.classes.border.neutral} max-w-md w-full`}
        style={{
          borderRadius: dynastyTheme.tokens.radius.lg,
          padding: dynastyTheme.tokens.spacing.xl,
        }}
      >
        <div className="text-center" style={{ marginBottom: dynastyTheme.tokens.spacing.lg }}>
          <div
            className={`flex items-center justify-center mx-auto ${dynastyTheme.classes.bg.warning} ${dynastyTheme.classes.shadow.base}`}
            style={{
              width: '3rem',
              height: '3rem',
              borderRadius: dynastyTheme.tokens.radius.lg,
              marginBottom: dynastyTheme.tokens.spacing.md,
            }}
          >
            <AlertTriangle className={dynastyTheme.classes.text.black} style={{ width: '1.5rem', height: '1.5rem' }} />
          </div>
          <h3 className={`${dynastyTheme.components.heading.h2} ${dynastyTheme.classes.text.white} mb-2`}>
            Activate {playerName}?
          </h3>
        </div>

        <div
          className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-6`}
          style={{ lineHeight: '1.5' }}
        >
          <p style={{ marginBottom: dynastyTheme.tokens.spacing.md }}>
            <span className={dynastyTheme.classes.text.white}>{playerName}</span> has never been activated.
            Moving them to {destinationLabel} will trigger a standard ML promotion contract:
          </p>
          <ul
            className={dynastyTheme.classes.text.white}
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              marginBottom: dynastyTheme.tokens.spacing.md,
            }}
          >
            <li>
              <strong>Salary:</strong> ${cost}
            </li>
            <li>
              <strong>Contract Years:</strong> {contractYears}
            </li>
          </ul>
          <p>Reversing requires commissioner action. Are you sure you want to proceed?</p>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            autoFocus
            onClick={onCancel}
            className={`flex-1 ${dynastyTheme.utils.getComponent('button', 'secondary', 'md')} justify-center`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`flex-1 ${dynastyTheme.utils.getComponent('button', 'primary', 'md')} justify-center`}
          >
            Activate
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromotionConfirmModal;
